// src/components/founderDashboard/GrowthDashboard.tsx
// Founder-only growth dashboard: content performance (both IG accounts),
// account growth, and cross-channel overlays (App Store, RevenueCat, mentions).
// Data comes from /founder-portal/growth-metrics/, which reads the private
// "Burst Metrics" Google Sheet server-side (15-min cache).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ResponsiveContainer, LineChart, Line, BarChart, Bar, ScatterChart, Scatter, LabelList,
    XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceArea,
    ComposedChart, Area,
} from "recharts";
import { api } from "./apiClient";
import {
    C, FONT, numStyle, microLabel, ACCT, num, parseDate, dayKey, median, fmt, shortDate, monthFormatter,
    RangePicker, RangeSel, LIVE_DATE, Shell, KpiStrip, Card, Grid, SectionHead, SectionNav,
    Legend, BarRow, Empty, Panel, Seg, axisX, axisY, gridProps, tipBar, tipLine, TipBox,
    th, td, tdNum, TableWrap,
} from "./hq";

type Row = Record<string, any>;
type Tabs = Record<string, Row[]>;

const pct = (v: number | null): number | null =>
    v === null ? null : v <= 1.5 ? v * 100 : v; // skip_rate / %non-followers arrive on mixed scales

type Post = {
    id: string; date: Date; day: string; account: "Burst" | "Personal";
    title: string; type: string; category: string; style: string;
    views: number | null; reach: number | null; likes: number | null;
    comments: number | null; follows: number | null; saves: number | null;
    shares: number | null; skip: number | null; visits: number | null;
    watch: number | null; bioTaps: number | null; eng: number | null;
    visitsPer1k: number | null;
};

const toPost = (r: Row): Post | null => {
    const date = parseDate(r.date);
    if (!date) return null;
    const acctRaw = String(r.account || "").trim();
    const account: "Burst" | "Personal" = acctRaw === "Personal" ? "Personal" : "Burst";
    const t = String(r.post_type || "").toUpperCase();
    const type = t.includes("REEL") ? "Reel" : t.includes("CAROUSEL") ? "Carousel" : t.includes("IMAGE") ? "Image" : (r.post_type || "?");
    const views = num(r.views), visits = num(r.profile_visits);
    const likes = num(r.likes), comments = num(r.comments), saves = num(r.saves), shares = num(r.shares);
    const engParts = [likes, comments, saves, shares].filter((x): x is number => x !== null);
    return {
        id: String(r.media_id || `${r.date}_${r.title}`), date, day: dayKey(date), account,
        title: String(r.title || ""), type, category: String(r.category || "").trim(),
        style: String(r.style || "").trim(),
        views, reach: num(r.reach), likes, comments, follows: num(r.follows),
        saves, shares, skip: pct(num(r.skip_rate)), visits,
        watch: num(r.avg_watch_time_s), bioTaps: num(r.bio_link_taps),
        eng: engParts.length ? engParts.reduce((a, b) => a + b, 0) : null,
        visitsPer1k: views && visits !== null ? (visits / views) * 1000 : null,
    };
};

// Change vs the previous equal period, as a pill.
const DeltaPill: React.FC<{ cur: number; prev: number | null }> = ({ cur, prev }) => {
    const base: React.CSSProperties = { fontFamily: FONT, fontWeight: 600, fontSize: 12, padding: "4px 9px", borderRadius: 999, whiteSpace: "nowrap" };
    if (prev === null) return <span style={{ ...base, color: C.muted, background: C.inset }}>all time</span>;
    if (prev === 0) return <span style={{ ...base, color: C.muted, background: C.inset }}>no prior-period data</span>;
    const d = ((cur - prev) / prev) * 100;
    const up = d >= 0;
    return (
        <span style={{ ...base, color: up ? C.sageD : C.alert, background: up ? "rgba(144,155,123,.16)" : "rgba(169,71,47,.1)" }}>
            {up ? "▲" : "▼"} {fmt(Math.abs(d))}% vs prev
        </span>
    );
};
const deltaText = (cur: number, prev: number | null | undefined) => {
    if (prev === undefined) return " ";
    if (prev === null) return "all time";
    if (prev === 0) return "no prior-period data";
    const d = ((cur - prev) / prev) * 100;
    return `${d >= 0 ? "▲" : "▼"} ${fmt(Math.abs(d))}% vs prev`;
};

// Category colours, assigned by volume across ALL posts so a category keeps
// its colour whatever the filters.
const CAT_PALETTE = [C.rust, C.slate, C.mauve, C.gold, C.brown, C.sky];

const SECTIONS = [
    { id: "content", n: "01", label: "Content" },
    { id: "stores", n: "02", label: "App stores" },
    { id: "acquisition", n: "03", label: "Acquisition" },
    { id: "paid", n: "04", label: "Paid" },
    { id: "posts", n: "05", label: "Posts" },
];

const LISTING_UPDATE = "2026-08-25";

export default function GrowthDashboard() {
    const [tabs, setTabs] = useState<Tabs | null>(null);
    const [err, setErr] = useState<string | null>(null);

    const [range, setRange] = useState<RangeSel>({ kind: "preset", days: 90 });
    const [account, setAccount] = useState("All");
    const [category, setCategory] = useState("All");
    const [format, setFormat] = useState("All");
    const [query, setQuery] = useState("");
    const [yMode, setYMode] = useState<"per1k" | "raw">("per1k");
    const [adSortKey, setAdSortKey] = useState("spend");
    const [adSortDir, setAdSortDir] = useState<"asc" | "desc">("desc");
    const [expandedAd, setExpandedAd] = useState<string | null>(null);
    const [xScale, setXScale] = useState<"log" | "linear">("log");
    const kFmt = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(v));
    const [sortKey, setSortKey] = useState<keyof Post>("date");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

    useEffect(() => {
        api.get("/founder-portal/growth-metrics/")
            .then(res => setTabs(res.data.tabs))
            .catch(e => setErr(e?.response?.data?.error || e?.message || "Failed to load"));
    }, []);

    const now = useMemo(() => new Date(), []);
    const { winStart, winEnd, windowDays } = useMemo(() => {
        if (range.kind === "all") return { winStart: null as Date | null, winEnd: null as Date | null, windowDays: null as number | null };
        if (range.kind === "since") {
            const st = parseDate(LIVE_DATE)!;
            const days = Math.max(1, Math.round((now.getTime() - st.getTime()) / 86400000) + 1);
            return { winStart: st, winEnd: null as Date | null, windowDays: days as number | null };
        }
        if (range.kind === "preset") {
            const d = new Date(now); d.setUTCDate(d.getUTCDate() - range.days);
            return { winStart: d, winEnd: null as Date | null, windowDays: range.days as number | null };
        }
        const st = parseDate(range.start), en = parseDate(range.end);
        const days = st && en ? Math.max(1, Math.round((en.getTime() - st.getTime()) / 86400000) + 1) : null;
        return { winStart: st, winEnd: en, windowDays: days };
    }, [range, now]);
    const inWindow = useCallback(
        (d: Date) => (!winStart || d >= winStart) && (!winEnd || d <= winEnd),
        [winStart, winEnd]);

    // ---- parse tabs once
    const allPosts = useMemo(() =>
        (tabs?.ig_posts || []).map(toPost).filter((p): p is Post => p !== null), [tabs]);
    const acctDaily = useMemo(() => (tabs?.ig_account_daily || []).map(r => ({
        date: parseDate(r.date), account: String(r.account || "").trim(),
        followersTotal: num(r.followers_total), follows: num(r.follows_added),
        reach: num(r.reach),
    })).filter(r => r.date), [tabs]);
    const asc = useMemo(() => (tabs?.asc_daily || []).map(r => ({
        date: parseDate(r.date), impressions: num(r.impressions),
        pv: num(r.product_page_views), dl: num(r.downloads),
    })).filter(r => r.date), [tabs]);
    const play = useMemo(() => (tabs?.play_daily || []).map(r => ({
        date: parseDate(r.date),
        impressions: num(r.impressions) ?? num(r.store_listing_impressions),
        pv: num(r.store_listing_visitors), dl: num(r.store_listing_acquisitions),
    })).filter(r => r.date), [tabs]);
    const regs = useMemo(() => (tabs?.registrations_daily || []).map(r => ({
        date: parseDate(r.date), n: num(r.registrations),
    })).filter(r => r.date), [tabs]);
    const rc = useMemo(() => (tabs?.rc_monthly || []).map(r => ({
        month: parseDate(r.month), trials: num(r.new_trials), paid: num(r.new_paid_subs),
        churned: num(r.churned), active: num(r.active_subs_eom), revenue: num(r.revenue_gbp),
    })).filter(r => r.month), [tabs]);
    const mentions = useMemo(() => (tabs?.mentions || []).map(r => ({
        date: parseDate(r.date), by: String(r.mentioned_by || ""),
        kind: String(r.content_type || ""),
    })).filter(r => r.date), [tabs]);
    const targets = useMemo(() => (tabs?.targets || []).map(r => ({
        category: String(r.category || ""), perWeek: num(r.posts_per_week),
        from: parseDate(r.active_from),
    })).filter(t => t.category), [tabs]);
    const rcDaily = useMemo(() => (tabs?.rc_daily || []).map(r => ({
        date: parseDate(r.date), trials: num(r.active_trials),
        subs: num(r.active_subscriptions), mrr: num(r.mrr),
    })).filter(r => r.date).sort((a, b) => a.date!.getTime() - b.date!.getTime()), [tabs]);
    const ascDisc = useMemo(() => (tabs?.asc_discovery_engagement_standard || []).map(r => ({
        date: parseDate(r.Date), event: String(r.Event || ""), source: String(r["Source Type"] || "?"),
        device: String(r.Device || "?"), counts: num(r.Counts) || 0,
    })).filter(r => r.date), [tabs]);
    const ascInst = useMemo(() => (tabs?.asc_installation_deletion_standard || []).map(r => ({
        date: parseDate(r.Date), event: String(r.Event || ""), dtype: String(r["Download Type"] || ""),
        source: String(r["Source Type"] || "?"), device: String(r.Device || "?"), counts: num(r.Counts) || 0,
    })).filter(r => r.date), [tabs]);

    // ---- filtered post sets
    const accountPosts = useMemo(() => allPosts.filter(p =>
        (account === "All" || p.account === account) && inWindow(p.date)), [allPosts, account, inWindow]);
    const posts = useMemo(() => accountPosts.filter(p =>
        (category === "All" || p.category === category) &&
        (format === "All" || p.type === format) &&
        (!query.trim() || (p.title + " " + p.category + " " + p.style).toLowerCase().includes(query.trim().toLowerCase()))),
        [accountPosts, category, format, query]);
    const categories = useMemo(() =>
        Array.from(new Set(allPosts.map(p => p.category).filter(Boolean))).sort(), [allPosts]);

    // ---- per-post views vs visits, coloured by category (top by volume; rest neutral)
    const catColorMap = useMemo(() => {
        const counts = new Map<string, number>();
        allPosts.forEach(p => { if (p.category) counts.set(p.category, (counts.get(p.category) || 0) + 1); });
        const top = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, CAT_PALETTE.length).map(e => e[0]);
        const m = new Map<string, string>();
        top.forEach((c, i) => m.set(c, CAT_PALETTE[i]));
        return m;
    }, [allPosts]);
    const scatterGroups = useMemo(() => {
        const pts = posts.filter(p => p.views !== null && p.views > 0 && p.visits !== null)
            .map(p => ({ x: p.views!, rawVisits: p.visits!, v1k: p.visitsPer1k ?? 0,
                         cat: p.category || "Untagged", title: p.title }));
        const groups = Array.from(catColorMap.entries())
            .map(([name, color]) => ({ name, color, data: pts.filter(d => d.cat === name) }))
            .filter(g => g.data.length);
        const other = pts.filter(d => !catColorMap.has(d.cat));
        if (other.length) groups.push({ name: "Other", color: C.neutral, data: other });
        return groups;
    }, [posts, catColorMap]);

    // ---- KPIs over window
    const kpi = useMemo(() => {
        const v = (xs: (number | null)[]) => xs.reduce((a: number, b) => a + (b || 0), 0);
        const ad = acctDaily.filter(r => r.date && inWindow(r.date!) && (account === "All" || r.account === account));
        const ascW = asc.filter(r => inWindow(r.date!));
        const playW = play.filter(r => inWindow(r.date!));
        const regsW = regs.filter(r => inWindow(r.date!));
        const latestActive = rc.length ? rc[rc.length - 1].active : null;
        const views = v(accountPosts.map(p => p.views));
        const visits = v(accountPosts.map(p => p.visits));
        return {
            posts: accountPosts.length, views,
            visitsPer1k: views ? (visits / views) * 1000 : null,
            follows: v(ad.map(r => r.follows)),
            downloads: v(ascW.map(r => r.dl)) + v(playW.map(r => r.dl)),
            registrations: v(regsW.map(r => r.n)),
            activeSubs: latestActive,
        };
    }, [accountPosts, acctDaily, asc, play, regs, rc, account, inWindow]);

    // ---- category matrix
    const catMatrix = useMemo(() => {
        const out: { category: string; account: string; medViews: number; visitsPer1k: number; rawVisits: number; n: number }[] = [];
        const groups = new Map<string, Post[]>();
        accountPosts.filter(p => p.category && p.views).forEach(p => {
            const k = `${p.account}|${p.category}`;
            groups.set(k, [...(groups.get(k) || []), p]);
        });
        groups.forEach((g, k) => {
            const [acct, cat] = k.split("|");
            const totV = g.reduce((a, p) => a + (p.views || 0), 0);
            const totVis = g.reduce((a, p) => a + (p.visits || 0), 0);
            if (totV <= 0) return;
            out.push({
                category: cat, account: acct, n: g.length,
                medViews: Math.max(1, median(g.map(p => p.views!))),
                visitsPer1k: totV ? (totVis / totV) * 1000 : 0,
                rawVisits: totVis,
            });
        });
        return out;
    }, [accountPosts]);

    // ---- format medians per account
    const formatStats = useMemo(() => {
        const out: Row[] = [];
        ["Reel", "Carousel"].forEach(t => {
            const row: Row = { type: t };
            ["Burst", "Personal"].forEach(a => {
                const g = accountPosts.filter(p => p.type === t && p.account === a && p.views);
                row[a] = g.length ? median(g.map(p => p.views!)) : null;
                row[`${a}_n`] = g.length;
            });
            out.push(row);
        });
        return out;
    }, [accountPosts]);

    // ---- hook quality (reels)
    const hookData = useMemo(() => accountPosts
        .filter(p => p.type === "Reel" && p.watch !== null && p.skip !== null && p.skip > 5)
        .map(p => ({ x: p.watch, y: p.skip, account: p.account, title: p.title, views: p.views })), [accountPosts]);
    const hookXMax = useMemo(() => Math.max(10, Math.ceil(Math.max(0, ...hookData.map(h => h.x || 0)) / 5) * 5), [hookData]);

    // ---- % non-follower reach by category (uses manual column where present)
    const nonFollower = useMemo(() => {
        const groups = new Map<string, number[]>();
        (tabs?.ig_posts || []).forEach(r => {
            const p = toPost(r); const nf = pct(num(r.pct_non_followers));
            if (!p || !p.category || nf === null || !inWindow(p.date)) return;
            if (account !== "All" && p.account !== account) return;
            groups.set(p.category, [...(groups.get(p.category) || []), nf]);
        });
        return Array.from(groups.entries())
            .map(([category, xs]) => ({ category, value: median(xs), n: xs.length }))
            .sort((a, b) => b.value - a.value);
    }, [tabs, account, inWindow]);

    // ---- followers over time (reconstructed backwards from latest totals)
    const followerSeries = useMemo(() => {
        const byAcct: Record<string, { day: string; adds: number | null; total: number | null }[]> = {};
        ["Burst", "Personal"].forEach(a => {
            const rows = acctDaily.filter(r => r.account === a && r.date)
                .sort((x, y) => x.date!.getTime() - y.date!.getTime());
            let anchorIdx = -1;
            for (let i = rows.length - 1; i >= 0; i--) if (rows[i].followersTotal !== null) { anchorIdx = i; break; }
            const out = rows.map(r => ({ day: dayKey(r.date!), adds: r.follows, total: r.followersTotal }));
            if (anchorIdx >= 0) {
                let running = rows[anchorIdx].followersTotal!;
                for (let i = anchorIdx - 1; i >= 0; i--) {
                    running -= rows[i + 1].follows || 0;
                    if (out[i].total === null) out[i].total = running;
                }
            }
            byAcct[a] = out;
        });
        const days = Array.from(new Set([...byAcct.Burst, ...byAcct.Personal].map(r => r.day))).sort();
        return days.map(day => ({
            day,
            Burst: byAcct.Burst.find(r => r.day === day)?.total ?? null,
            Personal: byAcct.Personal.find(r => r.day === day)?.total ?? null,
        })).filter(r => (!winStart || r.day >= dayKey(winStart)) && (!winEnd || r.day <= dayKey(winEnd)));
    }, [acctDaily, winStart, winEnd]);
    const latestFollowers = (a: "Burst" | "Personal") => {
        for (let i = followerSeries.length - 1; i >= 0; i--) if (followerSeries[i][a] !== null) return followerSeries[i][a];
        return null;
    };

    // ---- daily follows + post markers
    const followsDaily = useMemo(() => {
        const days = new Map<string, Row>();
        acctDaily.filter(r => r.date && inWindow(r.date!)).forEach(r => {
            const d = days.get(dayKey(r.date!)) || { day: dayKey(r.date!) };
            d[r.account] = r.follows; days.set(dayKey(r.date!), d);
        });
        allPosts.forEach(p => {
            if (!inWindow(p.date)) return;
            const d = days.get(p.day); if (d) d[`posted_${p.account}`] = true;
        });
        return Array.from(days.values()).sort((a, b) => a.day.localeCompare(b.day));
    }, [acctDaily, allPosts, inWindow]);

    // ---- acquisition timeline (two stacked panels, shared x)
    const timeline = useMemo(() => {
        const days = new Map<string, Row>();
        const touch = (d: Date) => {
            const k = dayKey(d);
            if (!days.has(k)) days.set(k, { day: k });
            return days.get(k)!;
        };
        acctDaily.forEach(r => { if (r.date && inWindow(r.date) && r.reach !== null) touch(r.date)[`reach${r.account}`] = r.reach; });
        asc.forEach(r => { if (r.date && inWindow(r.date!)) { const d = touch(r.date!); d.pv = (d.pv || 0) + (r.pv || 0); d.dl = (d.dl || 0) + (r.dl || 0); } });
        play.forEach(r => { if (r.date && inWindow(r.date!)) { const d = touch(r.date!); d.pv = (d.pv || 0) + (r.pv || 0); d.dl = (d.dl || 0) + (r.dl || 0); } });
        regs.forEach(r => { if (r.date && inWindow(r.date!)) touch(r.date!).regs = r.n; });
        return Array.from(days.values()).sort((a, b) => a.day.localeCompare(b.day));
    }, [acctDaily, asc, play, regs, inWindow]);
    const mentionDays = useMemo(() => mentions.filter(m => m.date && inWindow(m.date!)).map(m => ({
        day: dayKey(m.date!), link: /link/i.test(m.kind),
    })), [mentions, inWindow]);

    // ---- App stores section: Apple + Google, window totals + prev-period delta
    const store = useMemo(() => {
        type Tot = { impressions: number; pv: number; dl: number };
        const tot = (rows: { impressions: number | null; pv: number | null; dl: number | null }[]): Tot => ({
            impressions: rows.reduce((a, r) => a + (r.impressions || 0), 0),
            pv: rows.reduce((a, r) => a + (r.pv || 0), 0),
            dl: rows.reduce((a, r) => a + (r.dl || 0), 0),
        });
        const aW = asc.filter(r => r.date && inWindow(r.date));
        const gW = play.filter(r => r.date && inWindow(r.date));
        const apple = tot(aW), google = tot(gW);
        let prevApple: Tot | null = null, prevGoogle: Tot | null = null;
        if (windowDays !== null && winStart) {
            const prevStart = new Date(winStart);
            prevStart.setUTCDate(prevStart.getUTCDate() - windowDays);
            prevApple = tot(asc.filter(r => r.date && r.date >= prevStart && r.date < winStart));
            prevGoogle = tot(play.filter(r => r.date && r.date >= prevStart && r.date < winStart));
        }
        const days = new Map<string, Row>();
        const touch = (d: Date) => {
            const k = dayKey(d);
            if (!days.has(k)) days.set(k, { day: k });
            return days.get(k)!;
        };
        aW.forEach(r => { const d = touch(r.date!); d.aImp = r.impressions; d.aPv = r.pv; d.aDl = r.dl; });
        gW.forEach(r => { const d = touch(r.date!); d.gImp = r.impressions; d.gPv = r.pv; d.gDl = r.dl; });
        return {
            apple, google, prevApple, prevGoogle,
            series: Array.from(days.values()).sort((x, y) => x.day.localeCompare(y.day)),
        };
    }, [asc, play, winStart, windowDays, inWindow]);

    // ---- store funnel conversion vs Health & Fitness benchmarks
    const conv = useMemo(() => {
        const aW = asc.filter(r => r.date && inWindow(r.date));
        const gW = play.filter(r => r.date && inWindow(r.date));
        const regsW = regs.filter(r => r.date && inWindow(r.date!));
        const sum = (rows: any[], k: string) => rows.reduce((a, r) => a + (r[k] || 0), 0);
        // Google impressions only exist from 01/05/2026 - pair views with
        // impression-covered days so the ratio isn't inflated by older traffic
        const gImpDays = gW.filter(r => r.impressions !== null);
        const pc = (n: number, d: number) => (d > 0 ? (n / d) * 100 : null);
        const a = { imp2view: pc(sum(aW, "pv"), sum(aW, "impressions")), view2dl: pc(sum(aW, "dl"), sum(aW, "pv")) };
        const g = { imp2view: pc(sum(gImpDays, "pv"), sum(gImpDays, "impressions")), view2dl: pc(sum(gW, "dl"), sum(gW, "pv")) };
        const c = {
            imp2view: pc(sum(aW, "pv") + sum(gImpDays, "pv"), sum(aW, "impressions") + sum(gImpDays, "impressions")),
            view2dl: pc(sum(aW, "dl") + sum(gW, "dl"), sum(aW, "pv") + sum(gW, "pv")),
            dl2reg: pc(sum(regsW, "n"), sum(aW, "dl") + sum(gW, "dl")),
        };
        // iOS listing update went live 25/08/2026; fixed baseline = the
        // free-launch era before it. Ignores the window picker on purpose.
        const UPDATE = parseDate(LISTING_UPDATE)!;
        const BASE_START = parseDate("2026-07-21")!;
        const aEra = asc.filter(r => r.date! >= UPDATE);
        const aBase = asc.filter(r => r.date! >= BASE_START && r.date! < UPDATE);
        const gEra = play.filter(r => r.date! >= UPDATE);
        const gBase = play.filter(r => r.date! >= BASE_START && r.date! < UPDATE);
        const rEra = regs.filter(r => r.date! >= UPDATE);
        const rBase = regs.filter(r => r.date! >= BASE_START && r.date! < UPDATE);
        const era = {
            imp2view: pc(sum(aEra, "pv"), sum(aEra, "impressions")),
            view2dl: pc(sum(aEra, "dl"), sum(aEra, "pv")),
            dl2reg: pc(sum(rEra, "n"), sum(aEra, "dl") + sum(gEra, "dl")),
        };
        const base = {
            imp2view: pc(sum(aBase, "pv"), sum(aBase, "impressions")),
            view2dl: pc(sum(aBase, "dl"), sum(aBase, "pv")),
            dl2reg: pc(sum(rBase, "n"), sum(aBase, "dl") + sum(gBase, "dl")),
        };
        return { a, g, c, era, base };
    }, [asc, play, regs, inWindow]);

    const ads = useMemo(() => (tabs?.ads_daily || []).map(r => ({
        date: parseDate(r.date), campaign: String(r.campaign_name || "?"),
        ad: String(r.ad_name || r.ad_id || "?"),
        spend: num(r.spend) || 0, impressions: num(r.impressions) || 0,
        clicks: num(r.inline_link_clicks) ?? num(r.clicks) ?? 0,
        installs: num(r.app_installs) || 0,
        trials: num(r.trials) || 0,
    })).filter(r => r.date), [tabs]);

    const paid = useMemo(() => {
        const win = ads.filter(r => inWindow(r.date!));
        const sum = (rows: typeof win, k: "spend" | "impressions" | "clicks" | "installs") =>
            rows.reduce((a, r) => a + (r[k] as number), 0);
        const cur = { spend: sum(win, "spend"), impressions: sum(win, "impressions"),
                      clicks: sum(win, "clicks"), installs: sum(win, "installs") };
        let prev: typeof cur | null = null;
        if (windowDays !== null && winStart) {
            const prevStart = new Date(winStart);
            prevStart.setUTCDate(prevStart.getUTCDate() - windowDays);
            const p = ads.filter(r => r.date! >= prevStart && r.date! < winStart);
            prev = { spend: sum(p, "spend"), impressions: sum(p, "impressions"),
                     clicks: sum(p, "clicks"), installs: sum(p, "installs") };
        }
        const days = new Map<string, { day: string; spend: number; clicks: number; installs: number }>();
        win.forEach(r => {
            const k = dayKey(r.date!);
            const d = days.get(k) || { day: k, spend: 0, clicks: 0, installs: 0 };
            d.spend += r.spend; d.clicks += r.clicks; d.installs += r.installs;
            days.set(k, d);
        });
        const camps = new Map<string, { name: string; spend: number; clicks: number; installs: number }>();
        win.forEach(r => {
            const c = camps.get(r.campaign) || { name: r.campaign, spend: 0, clicks: 0, installs: 0 };
            c.spend += r.spend; c.clicks += r.clicks; c.installs += r.installs;
            camps.set(r.campaign, c);
        });
        const adMap = new Map<string, { ad: string; campaign: string; spend: number; impressions: number; clicks: number; installs: number; trials: number }>();
        win.forEach(r => {
            const k = `${r.campaign}|${r.ad}`;
            const a = adMap.get(k) || { ad: r.ad, campaign: r.campaign, spend: 0, impressions: 0, clicks: 0, installs: 0, trials: 0 };
            a.spend += r.spend; a.impressions += r.impressions; a.clicks += r.clicks; a.installs += r.installs; a.trials += r.trials;
            adMap.set(k, a);
        });
        return {
            cur, prev,
            daily: Array.from(days.values()).sort((a, b) => a.day.localeCompare(b.day)),
            campaigns: Array.from(camps.values()).sort((a, b) => b.spend - a.spend),
            adRows: Array.from(adMap.values()).sort((a, b) => b.spend - a.spend),
            hasData: win.length > 0, everHadData: ads.length > 0,
        };
    }, [ads, winStart, windowDays, inWindow]);

    const adRowsSorted = useMemo(() => {
        const rows = paid.adRows.map(a => ({
            ...a, k: `${a.campaign}|${a.ad}`,
            ctr: a.impressions ? (a.clicks / a.impressions) * 100 : null,
            cpi: a.installs ? a.spend / a.installs : null,
            cpt: a.trials ? a.spend / a.trials : null,
        }));
        const dir = adSortDir === "asc" ? 1 : -1;
        return rows.sort((x: Row, y: Row) => {
            const xv = x[adSortKey], yv = y[adSortKey];
            if (typeof xv === "string" || typeof yv === "string")
                return String(xv ?? "").localeCompare(String(yv ?? "")) * dir;
            const xn = xv ?? -Infinity, yn = yv ?? -Infinity;
            return (xn < yn ? -1 : xn > yn ? 1 : 0) * dir;
        });
    }, [paid.adRows, adSortKey, adSortDir]);
    const showTrials = useMemo(() => paid.adRows.some(a => a.trials > 0), [paid.adRows]);
    const expandedDaily = useMemo(() => {
        if (!expandedAd) return [];
        return ads.filter(r => `${r.campaign}|${r.ad}` === expandedAd && r.date && inWindow(r.date))
            .map(r => ({
                day: dayKey(r.date!), spend: r.spend, clicks: r.clicks,
                installs: r.installs, trials: r.trials,
                ctr: r.impressions ? (r.clicks / r.impressions) * 100 : null,
            }))
            .sort((a, b) => a.day.localeCompare(b.day));
    }, [ads, expandedAd, inWindow]);

    // ---- granular listing breakdowns (Apple raw reports; data begins 27/07/2026)
    const listing = useMemo(() => {
        const disc = ascDisc.filter(r => inWindow(r.date!));
        const inst = ascInst.filter(r => inWindow(r.date!));
        const agg = (rows: { counts: number }[]) => rows.reduce((a, r) => a + r.counts, 0);
        const dims = new Map<string, { impressions: number; pv: number; firstTime: number }>();
        const dim = (k: string) => {
            if (!dims.has(k)) dims.set(k, { impressions: 0, pv: 0, firstTime: 0 });
            return dims.get(k)!;
        };
        disc.forEach(r => {
            if (r.event === "Impression") dim(r.source).impressions += r.counts;
            if (r.event === "Page view") dim(r.source).pv += r.counts;
        });
        inst.forEach(r => {
            if (r.event === "Install" && r.dtype === "First-time download") dim(r.source).firstTime += r.counts;
        });
        const sources = Array.from(dims.entries()).map(([name, v]) => ({ name, ...v }))
            .sort((a, b) => (b.impressions + b.pv * 10 + b.firstTime * 20) - (a.impressions + a.pv * 10 + a.firstTime * 20));
        const devMap = new Map<string, { impressions: number; pv: number }>();
        disc.forEach(r => {
            const d = devMap.get(r.device) || { impressions: 0, pv: 0 };
            if (r.event === "Impression") d.impressions += r.counts;
            if (r.event === "Page view") d.pv += r.counts;
            devMap.set(r.device, d);
        });
        const devices = Array.from(devMap.entries()).map(([name, v]) => ({ name, ...v }))
            .sort((a, b) => b.impressions - a.impressions);
        const firstTime = agg(inst.filter(r => r.event === "Install" && r.dtype === "First-time download"));
        const updates = agg(inst.filter(r => r.event === "Install" && r.dtype === "Manual update"));
        const deletes = agg(inst.filter(r => r.event === "Delete"));
        return { sources, devices, firstTime, updates, deletes,
                 hasData: disc.length > 0 || inst.length > 0 };
    }, [ascDisc, ascInst, inWindow]);

    // ---- mention impact
    const mentionImpact = useMemo(() => {
        const linkDays = new Set(mentions.filter(m => /link/i.test(m.kind)).map(m => dayKey(m.date!)));
        const overlayDays = new Set(mentions.filter(m => !/link/i.test(m.kind)).map(m => dayKey(m.date!)));
        const since = mentions.length ? Math.min(...mentions.map(m => m.date!.getTime())) : 0;
        const rel = asc.filter(r => r.date && r.date.getTime() >= since);
        const bucket = (pred: (k: string) => boolean) => {
            const g = rel.filter(r => pred(dayKey(r.date!)));
            return g.length ? g.reduce((a, r) => a + (r.dl || 0), 0) / g.length : 0;
        };
        return [
            { name: "Jack app-link day", value: bucket(k => linkDays.has(k)), n: rel.filter(r => linkDays.has(dayKey(r.date!))).length, color: C.alert },
            { name: "Jack overlay day", value: bucket(k => overlayDays.has(k) && !linkDays.has(k)), n: rel.filter(r => overlayDays.has(dayKey(r.date!)) && !linkDays.has(dayKey(r.date!))).length, color: C.rust },
            { name: "No mention", value: bucket(k => !linkDays.has(k) && !overlayDays.has(k)), n: rel.filter(r => !linkDays.has(dayKey(r.date!)) && !overlayDays.has(dayKey(r.date!))).length, color: C.neutral },
        ];
    }, [mentions, asc]);

    // ---- targets vs actual (last full 7 days)
    const targetActual = useMemo(() => {
        const weekStart = new Date(now); weekStart.setUTCDate(weekStart.getUTCDate() - 7);
        const recent = allPosts.filter(p => p.date >= weekStart);
        return targets.map(t => ({
            category: t.category, target: t.perWeek || 0,
            actual: recent.filter(p => p.category === t.category).length,
        }));
    }, [targets, allPosts, now]);

    // ---- monthly funnel
    const monthly = useMemo(() => {
        const byMonth = new Map<string, Row>();
        asc.forEach(r => {
            if (!r.date) return;
            const k = r.date.toISOString().slice(0, 7);
            const d = byMonth.get(k) || { month: k, downloads: 0, registrations: 0 };
            d.downloads += r.dl || 0; byMonth.set(k, d);
        });
        play.forEach(r => {
            if (!r.date) return;
            const k = r.date.toISOString().slice(0, 7);
            const d = byMonth.get(k) || { month: k, downloads: 0, registrations: 0 };
            d.downloads += r.dl || 0; byMonth.set(k, d);
        });
        regs.forEach(r => {
            const k = r.date!.toISOString().slice(0, 7);
            const d = byMonth.get(k) || { month: k, downloads: 0, registrations: 0 };
            d.registrations += r.n || 0; byMonth.set(k, d);
        });
        rc.forEach(r => {
            const k = r.month!.toISOString().slice(0, 7);
            const d = byMonth.get(k) || { month: k, downloads: 0, registrations: 0 };
            d.trials = r.trials; d.paid = r.paid; d.active = r.active; byMonth.set(k, d);
        });
        return Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month)).slice(-9);
    }, [asc, play, regs, rc]);
    const monthTick = useMemo(() => monthFormatter(monthly.map(m => m.month)), [monthly]);

    // ---- post table
    const sorted = useMemo(() => {
        const dir = sortDir === "asc" ? 1 : -1;
        return [...posts].sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            if (typeof av === "string" || typeof bv === "string")
                return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
            const ax = av instanceof Date ? av.getTime() : (av ?? -Infinity);
            const bx = bv instanceof Date ? bv.getTime() : (bv ?? -Infinity);
            return (ax < bx ? -1 : ax > bx ? 1 : 0) * dir;
        });
    }, [posts, sortKey, sortDir]);
    const postTh = (label: string, key: keyof Post, align: "left" | "right" = "right") => (
        <th key={label} onClick={() => {
            if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
            else { setSortKey(key); setSortDir("desc"); }
        }} style={{ ...th(align, sortKey === key), cursor: "pointer", userSelect: "none" }}>
            {label}{sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
        </th>
    );

    const picker = <RangePicker range={range} onChange={setRange} presets={[30, 90, 180]} />;
    if (err) return <Shell title="Growth" right={picker}><Empty>{err}</Empty></Shell>;
    if (!tabs) return <Shell title="Growth" right={picker}><div style={{ color: C.muted, fontFamily: FONT }}>Loading growth data…</div></Shell>;

    const acctColor = (a: string) => ACCT[a] || C.slate;
    const scaleToggles = (<>
        <Seg mini options={["log", "linear"]} value={xScale} onChange={v => setXScale(v as "log" | "linear")} />
        <Seg mini options={["per 1k", "raw"]} value={yMode === "per1k" ? "per 1k" : "raw"} onChange={v => setYMode(v === "per 1k" ? "per1k" : "raw")} />
    </>);
    const acctLegend = <Legend items={[{ label: "Burst", color: ACCT.Burst, kind: "dot" }, { label: "Personal", color: ACCT.Personal, kind: "dot" }]} />;
    const xDomain: [any, any] = xScale === "log" ? ["auto", "auto"] : [0, "auto"];
    const adHeaders: { k: string; label: string; right: boolean }[] = [
        { k: "ad", label: "Ad", right: false },
        { k: "campaign", label: "Campaign", right: false },
        { k: "spend", label: "Spend", right: true },
        { k: "impressions", label: "Impr.", right: true },
        { k: "clicks", label: "Clicks", right: true },
        { k: "ctr", label: "CTR", right: true },
        { k: "installs", label: "Installs", right: true },
        { k: "cpi", label: "£/install", right: true },
        ...(showTrials ? [{ k: "trials", label: "Trials", right: true }, { k: "cpt", label: "£/trial", right: true }] : []),
    ];
    const campMax = paid.campaigns.length ? paid.campaigns[0].spend : 0;
    const impactMax = Math.max(0.0001, ...mentionImpact.map(m => m.value));
    const noMention = mentionImpact[2].value;

    // Follows bar with a dot above it on days that account posted.
    const followShape = (acct: "Burst" | "Personal") => (p: any) => {
        const { x, y, width, height, payload } = p;
        const h = Math.max(0, height || 0);
        return (
            <g>
                {h > 0 && <rect x={x} y={y} width={width} height={h} fill={acctColor(acct)} rx={Math.min(2, width / 2)} />}
                {payload?.[`posted_${acct}`] && <circle cx={x + width / 2} cy={y - 5} r={2.2} fill={acctColor(acct)} />}
            </g>
        );
    };

    return (
        <Shell title="Growth" right={picker}>
            {/* filters - apply everywhere below */}
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <Seg options={["All", "Burst", "Personal"]} value={account} onChange={setAccount} />
                <Seg options={["All", "Reel", "Carousel"]} value={format} onChange={setFormat} />
                <select value={category} onChange={e => setCategory(e.target.value)} style={{
                    fontFamily: FONT, fontWeight: 600, fontSize: 13, color: C.ink, background: "#FFF",
                    border: "1px solid rgba(22,25,37,.15)", borderRadius: 12, padding: "8px 12px", maxWidth: 220,
                }}>
                    <option value="All">All categories</option>{categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input placeholder="Search posts…" value={query} onChange={e => setQuery(e.target.value)} style={{
                    marginLeft: "auto", flex: "1 1 200px", maxWidth: 280, fontFamily: FONT, fontSize: 13, color: C.ink,
                    background: "#FFF", border: "1px solid rgba(22,25,37,.15)", borderRadius: 12, padding: "9px 14px", outline: "none",
                }} />
            </div>

            <KpiStrip items={[
                { label: "Posts", value: fmt(kpi.posts) },
                { label: "Views", value: fmt(kpi.views) },
                { label: "Visits / 1k views", value: fmt(kpi.visitsPer1k, 1), sub: "profile pull" },
                { label: "Follows added", value: fmt(kpi.follows) },
                { label: "Downloads", value: fmt(kpi.downloads) },
                { label: "Registrations", value: fmt(kpi.registrations), sub: regs.length ? undefined : "paste SQL into sheet" },
                { label: "Active subs", value: fmt(kpi.activeSubs), sub: "latest month" },
            ]} />

            <SectionNav items={SECTIONS} />

            {/* ---------------- 01 content */}
            <SectionHead id="content" n="01" label="Content" title="What gets seen and what pulls people in" right={acctLegend} />
            <Grid min={300}>
                <Card title="Category matrix" right={scaleToggles}>
                    <ResponsiveContainer width="100%" height={240}>
                        <ScatterChart margin={{ top: 8, right: 8, bottom: 0, left: -6 }}>
                            <CartesianGrid stroke={C.grid} />
                            <XAxis {...axisX} dataKey="medViews" name="median views" type="number" scale={xScale} domain={xDomain} tickFormatter={kFmt} allowDataOverflow={false} />
                            <YAxis {...axisY} dataKey={yMode === "per1k" ? "visitsPer1k" : "rawVisits"} name={yMode === "per1k" ? "visits/1k" : "visits"} type="number" />
                            <ZAxis dataKey="n" range={[60, 400]} name="posts" />
                            <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => payload?.length ? (
                                <TipBox title={`${payload[0].payload.category} (${payload[0].payload.account})`}>
                                    median views {fmt(payload[0].payload.medViews)} · {fmt(payload[0].payload.rawVisits)} visits · {fmt(payload[0].payload.visitsPer1k, 1)}/1k · {payload[0].payload.n} posts
                                </TipBox>) : null} />
                            {["Burst", "Personal"].map(a => (
                                <Scatter key={a} name={a} data={catMatrix.filter(d => d.account === a)} fill={acctColor(a)} fillOpacity={0.8} stroke="#FFF" strokeWidth={1.5} />
                            ))}
                        </ScatterChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Views vs profile visits, per post" right={scaleToggles}>
                    <ResponsiveContainer width="100%" height={220}>
                        <ScatterChart margin={{ top: 8, right: 8, bottom: 0, left: -6 }}>
                            <CartesianGrid stroke={C.grid} />
                            <XAxis {...axisX} dataKey="x" name="views" type="number" scale={xScale} domain={xDomain} tickFormatter={kFmt} allowDataOverflow={false} />
                            <YAxis {...axisY} dataKey={yMode === "per1k" ? "v1k" : "rawVisits"} name={yMode === "per1k" ? "visits/1k" : "profile visits"} type="number" />
                            <Tooltip content={({ payload }) => payload?.length ? (
                                <TipBox title={payload[0].payload.title.slice(0, 60)}>
                                    {payload[0].payload.cat} · {fmt(payload[0].payload.x)} views · {fmt(payload[0].payload.rawVisits)} visits · {fmt(payload[0].payload.v1k, 1)}/1k
                                </TipBox>) : null} />
                            {scatterGroups.map(g => (
                                <Scatter key={g.name} name={g.name} data={g.data} fill={g.color} stroke="#FFF" strokeWidth={1.5} />
                            ))}
                        </ScatterChart>
                    </ResponsiveContainer>
                    <Legend items={scatterGroups.map(g => ({ label: g.name, color: g.color, kind: "dot" as const }))} />
                </Card>

                <Card title="Format: median views">
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={formatStats} margin={{ top: 20, right: 4, bottom: 0, left: -2 }} barGap={6} barCategoryGap="22%">
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="type" tick={{ ...axisX.tick, fontWeight: 600, fill: C.ink }} />
                            <YAxis {...axisY} width={40} tickFormatter={kFmt} />
                            <Tooltip {...tipBar} formatter={(v: any, name: any, item: any) =>
                                [`${fmt(v)} median views (${item.payload[`${name}_n`]} posts)`, name]} />
                            {(["Burst", "Personal"] as const).map(a => (
                                <Bar key={a} dataKey={a} fill={acctColor(a)} radius={[6, 6, 0, 0]} maxBarSize={64}>
                                    <LabelList dataKey={a} position="top" formatter={(v: any) => fmt(v)} style={{ ...numStyle(14), fill: C.ink }} />
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Hook quality, reels">
                    <ResponsiveContainer width="100%" height={240}>
                        <ScatterChart margin={{ top: 8, right: 8, bottom: 0, left: -4 }}>
                            <CartesianGrid stroke={C.grid} />
                            <ReferenceArea x1={hookXMax / 2} x2={hookXMax} y1={0} y2={50} fill="rgba(144,155,123,.12)" stroke="none"
                                label={{ value: "STRONG HOOKS", position: "insideBottomRight", fill: C.sageD, fontSize: 10, fontWeight: 600, fontFamily: FONT, letterSpacing: 1 }} />
                            <XAxis {...axisX} dataKey="x" name="watch s" type="number" domain={[0, hookXMax]} tickFormatter={(v: number) => `${v}s`} />
                            <YAxis {...axisY} dataKey="y" name="skip %" type="number" domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} width={38} />
                            <Tooltip content={({ payload }) => payload?.length ? (
                                <TipBox title={payload[0].payload.title.slice(0, 60)}>
                                    {fmt(payload[0].payload.x, 1)}s watch · {fmt(payload[0].payload.y)}% skip · {fmt(payload[0].payload.views)} views
                                </TipBox>) : null} />
                            {["Burst", "Personal"].map(a => (
                                <Scatter key={a} name={a} data={hookData.filter(d => d.account === a)} fill={acctColor(a)} stroke="#FFF" strokeWidth={1.5} />
                            ))}
                        </ScatterChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Discovery by category" gap={10}>
                    {nonFollower.length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 268, overflowY: "auto", paddingRight: 4 }}>
                            {nonFollower.map(r => (
                                <BarRow key={r.category} label={r.category} value={`${fmt(r.value)}%`} pct={r.value} labelWidth={130} valueWidth={40} height={10} />
                            ))}
                        </div>
                    ) : <Empty>No hand-logged non-follower figures in this window.</Empty>}
                </Card>

                <Card title="Followers" gap={12}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        {(["Burst", "Personal"] as const).map(a => (
                            <div key={a} style={{ background: C.panel, borderRadius: 12, padding: "10px 12px" }}>
                                <div style={{ ...microLabel, letterSpacing: "0.1em" }}>{a}</div>
                                <div style={{ ...numStyle(24), lineHeight: 1.1 }}>{fmt(latestFollowers(a))}</div>
                            </div>
                        ))}
                    </div>
                    <ResponsiveContainer width="100%" height={150}>
                        <LineChart data={followerSeries} margin={{ top: 6, right: 4, bottom: 0, left: -4 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="day" tickFormatter={shortDate} minTickGap={36} />
                            <YAxis {...axisY} width={38} domain={["auto", "auto"]} />
                            <Tooltip {...tipLine} labelFormatter={shortDate} />
                            <Line type="monotone" dataKey="Burst" stroke={ACCT.Burst} strokeWidth={2} dot={false} connectNulls />
                            <Line type="monotone" dataKey="Personal" stroke={ACCT.Personal} strokeWidth={2} dot={false} connectNulls />
                        </LineChart>
                    </ResponsiveContainer>
                </Card>
            </Grid>
            <Card title="Follows added per day"
                right={<Legend items={[{ label: "Burst", color: ACCT.Burst }, { label: "Personal", color: ACCT.Personal }, { label: "Posted that day", color: C.ink, kind: "dot" }]} />}>
                <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={followsDaily} margin={{ top: 10, right: 4, bottom: 0, left: -10 }} barGap={1} barCategoryGap="12%">
                        <CartesianGrid {...gridProps} />
                        <XAxis {...axisX} dataKey="day" tickFormatter={shortDate} minTickGap={36} />
                        <YAxis {...axisY} width={28} allowDecimals={false} />
                        <Tooltip {...tipBar} labelFormatter={shortDate} />
                        <Bar dataKey="Burst" fill={ACCT.Burst} shape={followShape("Burst")} />
                        <Bar dataKey="Personal" fill={ACCT.Personal} shape={followShape("Personal")} />
                    </BarChart>
                </ResponsiveContainer>
            </Card>

            {/* ---------------- 02 app stores */}
            <SectionHead id="stores" n="02" label="App stores" title="From store listing to download"
                right={<Legend items={[{ label: "Apple", color: C.ink }, { label: "Google", color: C.sage }, { label: "iOS listing update, 25 Aug", color: C.ink, kind: "dash" }]} />} />
            <Card gap={22}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 28 }}>
                    {([
                        { key: "impressions", label: "Impressions", a: "aImp", g: "gImp" },
                        { key: "pv", label: "Store listing views", a: "aPv", g: "gPv" },
                        { key: "dl", label: "Downloads", a: "aDl", g: "gDl" },
                    ] as const).map(m => {
                        const cur = store.apple[m.key] + store.google[m.key];
                        const prev = store.prevApple && store.prevGoogle ? store.prevApple[m.key] + store.prevGoogle[m.key] : null;
                        return (
                            <div key={m.key} style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "8px 10px", flexWrap: "wrap" }}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                        <div style={microLabel}>{m.label}</div>
                                        <div style={{ ...numStyle(40), lineHeight: 1 }}>{fmt(cur)}</div>
                                        <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>Apple {fmt(store.apple[m.key])} · Google {fmt(store.google[m.key])}</div>
                                    </div>
                                    <DeltaPill cur={cur} prev={prev} />
                                </div>
                                <ResponsiveContainer width="100%" height={130}>
                                    <BarChart data={store.series} margin={{ top: 4, right: 0, bottom: 0, left: -18 }} syncId="store" barCategoryGap="8%">
                                        <XAxis {...axisX} dataKey="day" tickFormatter={shortDate} minTickGap={40} />
                                        <YAxis {...axisY} width={40} allowDecimals={false} tickFormatter={kFmt} />
                                        <Tooltip {...tipBar} labelFormatter={shortDate} />
                                        <ReferenceLine x={LISTING_UPDATE} stroke="rgba(22,25,37,.5)" strokeDasharray="4 3" strokeWidth={1.5} />
                                        <Bar name="Apple" dataKey={m.a} stackId="s" fill={C.ink} />
                                        <Bar name="Google" dataKey={m.g} stackId="s" fill={C.sage} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        );
                    })}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: 14 }}>Conversion vs health &amp; fitness benchmarks</div>
                    <TableWrap minWidth={760}>
                        <thead>
                            <tr>
                                <th style={th()}>Step</th>
                                <th style={th("right")}>Apple</th>
                                <th style={th("right")}>Google</th>
                                <th style={th("right")}>Combined</th>
                                <th style={th("right")}>Since 25 Aug</th>
                                <th style={th("right")}>Benchmark</th>
                            </tr>
                        </thead>
                        <tbody>
                            {([
                                { label: "Impression → listing view *", a: conv.a.imp2view, g: conv.g.imp2view, c: conv.c.imp2view, era: conv.era.imp2view, base: conv.base.imp2view, lo: 6, bench: "6–12%" },
                                { label: "Listing view → download", a: conv.a.view2dl, g: conv.g.view2dl, c: conv.c.view2dl, era: conv.era.view2dl, base: conv.base.view2dl, lo: 18.5, bench: "18.5–30.8%" },
                                { label: "Download → registration †", a: null, g: null, c: conv.c.dl2reg, era: conv.era.dl2reg, base: conv.base.dl2reg, lo: 25, bench: "25–34%" },
                            ] as const).map(row => {
                                const cell = (v: number | null) => v === null
                                    ? <td style={tdNum(600, "rgba(22,25,37,.4)")}>-</td>
                                    : <td style={{ ...tdNum(), fontSize: 16, color: v < row.lo ? C.alert : C.ink }}>{fmt(v, 1)}%</td>;
                                return (
                                    <tr key={row.label} className="hq-row">
                                        <td style={{ ...td(), fontWeight: 500 }}>{row.label}</td>
                                        {cell(row.a)}{cell(row.g)}{cell(row.c)}
                                        <td style={td("right")}>
                                            {row.era === null ? <span style={{ color: C.faint }}>-</span> : (<>
                                                <span style={{ ...numStyle(16), color: row.base !== null && row.era >= row.base ? C.sageD : C.alert }}>{fmt(row.era, 1)}%</span>
                                                {row.base !== null && <span style={{ fontFamily: FONT, fontSize: 11, color: C.tick }}> was {fmt(row.base, 1)}%</span>}
                                            </>)}
                                        </td>
                                        <td style={tdNum(500, "rgba(22,25,37,.6)")}>{row.bench}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </TableWrap>
                    <div style={{ fontFamily: FONT, fontSize: 11, lineHeight: 1.5, color: C.muted, maxWidth: 1100 }}>
                        * Runs far above benchmark because referrer traffic (Instagram / Jack links) opens the listing without a counted impression: watch the trend, not the level.
                        {" †"} Registrations aren't store-attributed, so this only exists combined; it can exceed 100% when installs predate the window. Google impressions recorded from 1 May 2026.
                        {" "}<span style={{ color: C.alert, fontWeight: 600 }}>Red</span> = below benchmark. "Since 25 Aug" = the iOS listing update era (Apple rates; reg row combined), vs the 21 Jul–24 Aug free-launch baseline shown as "was"; <span style={{ color: C.sageD, fontWeight: 600 }}>green</span> means improved. Fixed dates, unaffected by the range picker; small samples early on.
                    </div>
                </div>
            </Card>

            {listing.hasData && (
                <Card title="Listing insights, Apple granular" gap={18}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))", gap: 28 }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <Panel label="First-time downloads" value={fmt(listing.firstTime)} />
                            <Panel label="Updates installed" value={fmt(listing.updates)} />
                            <Panel label="Deletions" value={fmt(listing.deletes)} color={C.alert} />
                        </div>
                        {([
                            { key: "impressions", label: "Impressions by source" },
                            { key: "pv", label: "Page views by source" },
                            { key: "firstTime", label: "First-time downloads by source" },
                        ] as const).map(m => {
                            const max = Math.max(1, ...listing.sources.map(s => s[m.key]));
                            return (
                                <div key={m.key} style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                                    <div style={{ ...microLabel, marginBottom: 6 }}>{m.label}</div>
                                    {listing.sources.map(s => (
                                        <div key={s.name} style={{ display: "flex", flexDirection: "column", gap: 4, padding: "6px 0", borderTop: `1px solid ${C.grid}` }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                                <span style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12 }}>{s.name}</span>
                                                <span style={numStyle(15)}>{fmt(s[m.key])}</span>
                                            </div>
                                            <div style={{ height: 6, borderRadius: 6, background: "rgba(22,25,37,.05)" }}>
                                                <div style={{ height: 6, borderRadius: 6, background: C.ink, width: `${(s[m.key] / max) * 100}%` }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                    {listing.devices.length > 0 && (() => {
                        const max = Math.max(1, ...listing.devices.map(d => Math.max(d.impressions, d.pv)));
                        return (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))", gap: "12px 28px", paddingTop: 14, borderTop: `1px solid ${C.hair}` }}>
                                <div style={microLabel}>Impressions vs page views by device</div>
                                <div style={{ gridColumn: "span 3", display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                                    {listing.devices.map(d => (
                                        <div key={d.name} style={{ display: "grid", gridTemplateColumns: "70px minmax(0,1fr)", gap: 12, alignItems: "center" }}>
                                            <div style={{ fontFamily: FONT, fontWeight: 600, fontSize: 12 }}>{d.name}</div>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                    <div style={{ height: 9, borderRadius: 3, background: C.ink, width: `${(d.impressions / max) * 80}%`, minWidth: d.impressions ? 2 : 0 }} />
                                                    <span style={{ ...numStyle(12, 500), color: "rgba(22,25,37,.6)" }}>{fmt(d.impressions)} impressions</span>
                                                </div>
                                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                    <div style={{ height: 9, borderRadius: 3, background: C.sage, width: `${(d.pv / max) * 80}%`, minWidth: d.pv ? 2 : 0 }} />
                                                    <span style={{ ...numStyle(12, 500), color: "rgba(22,25,37,.6)" }}>{fmt(d.pv)} page views</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                </Card>
            )}

            {/* ---------------- 03 acquisition */}
            <SectionHead id="acquisition" n="03" label="Acquisition" title="What drives downloads and subscriptions" />
            <Card title="Acquisition timeline" right={<Legend items={[
                { label: "Burst reach", color: ACCT.Burst, kind: "line" },
                { label: "Personal reach", color: ACCT.Personal, kind: "line" },
                { label: "Store page views", color: "#E8DFC7" },
                { label: "Downloads", color: C.rust, kind: "line" },
                { label: "Registrations", color: C.mauve, kind: "line" },
                { label: "Jack app-link mention", color: C.alert, kind: "vline" },
                { label: "Overlay mention", color: "rgba(169,71,47,.4)", kind: "vline" },
            ]} />}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ ...microLabel, color: C.tick, letterSpacing: "0.1em" }}>Instagram reach</div>
                    <ResponsiveContainer width="100%" height={160}>
                        <LineChart data={timeline} margin={{ top: 6, right: 4, bottom: 0, left: -2 }} syncId="acq">
                            <CartesianGrid {...gridProps} />
                            <XAxis dataKey="day" tick={false} axisLine={{ stroke: C.base }} height={2} />
                            <YAxis {...axisY} width={44} tickFormatter={kFmt} />
                            <Tooltip {...tipLine} labelFormatter={shortDate} />
                            {mentionDays.map((m, i) => (
                                <ReferenceLine key={i} x={m.day} stroke={C.alert} strokeOpacity={m.link ? 0.9 : 0.35} strokeDasharray={m.link ? undefined : "4 3"} />
                            ))}
                            <Line name="Burst reach" type="monotone" dataKey="reachBurst" stroke={ACCT.Burst} strokeWidth={2} dot={false} connectNulls />
                            <Line name="Personal reach" type="monotone" dataKey="reachPersonal" stroke={ACCT.Personal} strokeWidth={2} dot={false} connectNulls />
                        </LineChart>
                    </ResponsiveContainer>
                    <div style={{ ...microLabel, color: C.tick, letterSpacing: "0.1em", marginTop: 8 }}>Store page views · downloads · registrations</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <ComposedChart data={timeline} margin={{ top: 6, right: 4, bottom: 0, left: -2 }} syncId="acq">
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="day" tickFormatter={shortDate} minTickGap={36} />
                            <YAxis {...axisY} width={44} allowDecimals={false} tickFormatter={kFmt} />
                            <Tooltip {...tipLine} labelFormatter={shortDate} />
                            {mentionDays.map((m, i) => (
                                <ReferenceLine key={i} x={m.day} stroke={C.alert} strokeOpacity={m.link ? 0.9 : 0.35} strokeDasharray={m.link ? undefined : "4 3"} />
                            ))}
                            <Area name="Store page views" type="monotone" dataKey="pv" stroke="none" fill={C.sand} fillOpacity={1} connectNulls />
                            <Line name="Downloads" type="monotone" dataKey="dl" stroke={C.rust} strokeWidth={2} dot={false} />
                            <Line name="Registrations" type="monotone" dataKey="regs" stroke={C.mauve} strokeWidth={2} dot={false} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </Card>
            <Grid min={260}>
                <Card title="Subscriptions daily" right={<Legend items={[{ label: "Subscribers", color: C.ink, kind: "line" }, { label: "Trials", color: C.sage, kind: "line" }]} />}>
                    <ResponsiveContainer width="100%" height={170}>
                        <LineChart data={rcDaily.filter(r => inWindow(r.date!)).map(r => ({ day: dayKey(r.date!), Trials: r.trials, Subscribers: r.subs }))} margin={{ top: 6, right: 4, bottom: 0, left: -10 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="day" tickFormatter={shortDate} minTickGap={36} />
                            <YAxis {...axisY} width={28} allowDecimals={false} />
                            <Tooltip {...tipLine} labelFormatter={shortDate} />
                            <Line type="stepAfter" dataKey="Subscribers" stroke={C.ink} strokeWidth={2} dot={false} />
                            <Line type="stepAfter" dataKey="Trials" stroke={C.sage} strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Mention impact" gap={12}>
                    {mentionImpact.map(m => (
                        <div key={m.name} style={{ display: "flex", flexDirection: "column", gap: 5, padding: "2px 0" }} title={`${fmt(m.value, 1)} downloads/day over ${m.n} days`}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                                <span style={{ fontFamily: FONT, fontWeight: 600, fontSize: 13 }}>{m.name}</span>
                                <span style={{ ...numStyle(24), lineHeight: 1 }}>{fmt(m.value, 1)}</span>
                            </div>
                            <div style={{ height: 10, borderRadius: 4, background: "rgba(22,25,37,.05)" }}>
                                <div style={{ height: 10, borderRadius: 4, background: m.color, width: `${(m.value / impactMax) * 100}%` }} />
                            </div>
                        </div>
                    ))}
                    {noMention > 0 && mentionImpact[0].value > 0 && (
                        <div style={{ fontFamily: FONT, fontSize: 12, color: "rgba(22,25,37,.6)", marginTop: "auto" }}>
                            App-link days run at <b style={{ color: C.ink }}>{fmt(mentionImpact[0].value / noMention, 1)}{"×"}</b> no-mention days
                        </div>
                    )}
                </Card>

                <Card title="This week vs targets" gap={10}>
                    {targetActual.length ? targetActual.map(t => (
                        <div key={t.category} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 70px 46px", gap: 8, alignItems: "center" }}>
                            <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={t.category}>{t.category}</div>
                            <div style={{ height: 8, borderRadius: 8, background: C.hair }}>
                                <div style={{ height: 8, borderRadius: 8, background: C.sage, width: `${t.target ? Math.min(100, (t.actual / t.target) * 100) : 0}%` }} />
                            </div>
                            <div style={{ ...numStyle(13), textAlign: "right" }}>{fmt(t.actual)} / {fmt(t.target, 2)}</div>
                        </div>
                    )) : <Empty>No targets in the targets tab.</Empty>}
                </Card>

                <Card title="Monthly funnel">
                    <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={monthly} margin={{ top: 6, right: 4, bottom: 0, left: -10 }} barGap={1} barCategoryGap="18%">
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="month" tickFormatter={monthTick} />
                            <YAxis {...axisY} width={32} allowDecimals={false} />
                            <Tooltip {...tipBar} labelFormatter={monthTick} />
                            <Bar name="Downloads" dataKey="downloads" fill={C.gold} radius={[2, 2, 0, 0]} />
                            <Bar name="Registrations" dataKey="registrations" fill={C.mauve} radius={[2, 2, 0, 0]} />
                            <Bar name="Trials" dataKey="trials" fill={C.slate} radius={[2, 2, 0, 0]} />
                            <Bar name="New paid" dataKey="paid" fill={C.sage} radius={[2, 2, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    <Legend items={[{ label: "Downloads", color: C.gold }, { label: "Registrations", color: C.mauve }, { label: "Trials", color: C.slate }, { label: "New paid", color: C.sage }]} />
                </Card>
            </Grid>

            {/* ---------------- 04 paid */}
            <SectionHead id="paid" n="04" label="Paid" title="Meta ads" />
            {paid.everHadData ? (<>
                <KpiStrip size={34} items={[
                    { label: "Spend", value: `£${fmt(paid.cur.spend, 2)}`, sub: deltaText(paid.cur.spend, paid.prev?.spend ?? null) },
                    { label: "Impressions", value: fmt(paid.cur.impressions), sub: deltaText(paid.cur.impressions, paid.prev?.impressions ?? null) },
                    { label: "Link clicks", value: fmt(paid.cur.clicks), sub: deltaText(paid.cur.clicks, paid.prev?.clicks ?? null) },
                    { label: "CTR", value: paid.cur.impressions ? `${fmt((paid.cur.clicks / paid.cur.impressions) * 100, 2)}%` : "-", sub: " " },
                    { label: "Installs (SKAN)", value: fmt(paid.cur.installs), sub: deltaText(paid.cur.installs, paid.prev?.installs ?? null) },
                    { label: "Cost / install", value: paid.cur.installs ? `£${fmt(paid.cur.spend / paid.cur.installs, 2)}` : "-", sub: " " },
                ]} />
                <Grid min={240}>
                    {([
                        { key: "spend", label: "Spend per day", money: true },
                        { key: "clicks", label: "Link clicks per day", money: false },
                        { key: "installs", label: "Installs per day (SKAN)", money: false },
                    ] as const).map(m => {
                        const max = Math.max(0, ...paid.daily.map(d => d[m.key]));
                        return (
                            <Card key={m.key} title={m.label} right={<span style={{ fontSize: 12, color: C.muted }}>max {m.money ? `£${fmt(max, 0)}` : fmt(max)}</span>}>
                                <ResponsiveContainer width="100%" height={140}>
                                    <BarChart data={paid.daily} margin={{ top: 4, right: 4, bottom: 0, left: -10 }} syncId="ads" barCategoryGap="10%">
                                        <CartesianGrid {...gridProps} />
                                        <XAxis {...axisX} dataKey="day" tickFormatter={shortDate} minTickGap={40} />
                                        <YAxis {...axisY} width={34} tickFormatter={(v: number) => (m.money ? `£${v}` : fmt(v))} />
                                        <Tooltip {...tipBar} labelFormatter={shortDate}
                                            formatter={(v: any) => [m.money ? `£${fmt(v, 2)}` : fmt(v), m.label]} />
                                        <Bar name={m.label} dataKey={m.key} fill={C.rust} radius={[2, 2, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        );
                    })}
                    {paid.campaigns.length > 1 && (
                            <Card title="Spend by campaign" gap={10}>
                                {paid.campaigns.map(c => (
                                    <div key={c.name} style={{ display: "flex", flexDirection: "column", gap: 5, padding: "4px 0" }}
                                        title={`£${fmt(c.spend, 2)} · ${fmt(c.clicks)} clicks · ${fmt(c.installs)} installs${c.installs ? ` · £${fmt(c.spend / c.installs, 2)}/install` : ""}`}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                                            <span style={{ fontFamily: FONT, fontWeight: 500, fontSize: 13 }}>{c.name}</span>
                                            <span style={numStyle(17)}>{"£"}{fmt(c.spend)}</span>
                                        </div>
                                        <div style={{ height: 10, borderRadius: 4, background: "rgba(22,25,37,.05)" }}>
                                            <div style={{ height: 10, borderRadius: 4, background: C.rust, width: `${campMax ? (c.spend / campMax) * 100 : 0}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </Card>
                    )}
                </Grid>
                {paid.adRows.length > 0 && (
                        <Card title="Ad performance, selected window" gap={12}>
                            <TableWrap minWidth={showTrials ? 920 : 780}>
                                <thead>
                                    <tr>
                                        {adHeaders.map(h => (
                                            <th key={h.k} onClick={() => {
                                                if (adSortKey === h.k) setAdSortDir(d => d === "asc" ? "desc" : "asc");
                                                else { setAdSortKey(h.k); setAdSortDir("desc"); }
                                            }} style={{ ...th(h.right ? "right" : "left", adSortKey === h.k), cursor: "pointer", userSelect: "none" }}>
                                                {h.label}{adSortKey === h.k ? (adSortDir === "asc" ? " ↑" : " ↓") : ""}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {adRowsSorted.map((a: Row) => {
                                        const open = expandedAd === a.k;
                                        const cpiColor = a.cpi === null ? "rgba(22,25,37,.4)" : a.cpi <= 9 ? C.sageD : a.cpi >= 13 ? C.alert : C.ink;
                                        return (
                                            <React.Fragment key={a.k}>
                                                <tr className="hq-row" onClick={() => setExpandedAd(e => e === a.k ? null : a.k)}
                                                    style={{ cursor: "pointer", background: open ? "rgba(144,155,123,.10)" : undefined }}>
                                                    <td style={{ ...td(), fontWeight: 600, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.ad}>
                                                        <span style={{ color: C.muted, marginRight: 6 }}>{open ? "▾" : "▸"}</span>{a.ad}
                                                    </td>
                                                    <td style={{ ...td(), color: "rgba(22,25,37,.6)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.campaign}>{a.campaign}</td>
                                                    <td style={tdNum()}>{"£"}{fmt(a.spend, 2)}</td>
                                                    <td style={tdNum(500)}>{fmt(a.impressions)}</td>
                                                    <td style={tdNum(500)}>{fmt(a.clicks)}</td>
                                                    <td style={tdNum(500)}>{a.ctr === null ? "-" : `${fmt(a.ctr, 2)}%`}</td>
                                                    <td style={tdNum()}>{fmt(a.installs)}</td>
                                                    <td style={tdNum(600, cpiColor)}>{a.cpi === null ? "-" : `£${fmt(a.cpi, 2)}`}</td>
                                                    {showTrials && <td style={tdNum(500)}>{fmt(a.trials)}</td>}
                                                    {showTrials && <td style={tdNum()}>{a.cpt === null ? "-" : `£${fmt(a.cpt, 2)}`}</td>}
                                                </tr>
                                                {open && (
                                                    <tr>
                                                        <td colSpan={adHeaders.length} style={{ padding: "12px 8px 16px", background: "rgba(144,155,123,.06)", borderBottom: `1px solid ${C.rowLine}` }}>
                                                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 20 }}>
                                                                <div>
                                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                                                                        <div style={microLabel}>Spend &amp; CTR</div>
                                                                        <Legend items={[{ label: "Spend", color: C.rust }, { label: "CTR", color: C.ink, kind: "line" }]} />
                                                                    </div>
                                                                    <ResponsiveContainer width="100%" height={150}>
                                                                        <ComposedChart data={expandedDaily} margin={{ top: 4, right: 0, bottom: 0, left: -8 }} syncId="adDaily">
                                                                            <CartesianGrid {...gridProps} />
                                                                            <XAxis {...axisX} dataKey="day" tickFormatter={shortDate} minTickGap={30} />
                                                                            <YAxis {...axisY} yAxisId="l" width={36} tickFormatter={(v: number) => `£${v}`} />
                                                                            <YAxis {...axisY} yAxisId="r" orientation="right" width={34} tickFormatter={(v: number) => `${v}%`} />
                                                                            <Tooltip {...tipBar} labelFormatter={shortDate}
                                                                                formatter={(v: any, n: any) => n === "CTR" ? [`${fmt(v, 2)}%`, n] : n === "Spend" ? [`£${fmt(v, 2)}`, n] : [fmt(v), n]} />
                                                                            <Bar yAxisId="l" name="Spend" dataKey="spend" fill={C.rust} fillOpacity={0.55} radius={[2, 2, 0, 0]} maxBarSize={14} />
                                                                            <Line yAxisId="r" name="CTR" type="monotone" dataKey="ctr" stroke={C.ink} strokeWidth={2} dot={false} connectNulls />
                                                                        </ComposedChart>
                                                                    </ResponsiveContainer>
                                                                </div>
                                                                <div>
                                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                                                                        <div style={microLabel}>Clicks vs installs &amp; trials</div>
                                                                        <Legend items={[{ label: "Clicks", color: C.sageL2 }, { label: "Installs", color: C.rust, kind: "line" }, { label: "Trials", color: C.slate, kind: "line" }]} />
                                                                    </div>
                                                                    <ResponsiveContainer width="100%" height={150}>
                                                                        <ComposedChart data={expandedDaily} margin={{ top: 4, right: 0, bottom: 0, left: -8 }} syncId="adDaily">
                                                                            <CartesianGrid {...gridProps} />
                                                                            <XAxis {...axisX} dataKey="day" tickFormatter={shortDate} minTickGap={30} />
                                                                            <YAxis {...axisY} yAxisId="l" width={30} allowDecimals={false} />
                                                                            <YAxis {...axisY} yAxisId="r" orientation="right" width={26} allowDecimals={false} />
                                                                            <Tooltip {...tipBar} labelFormatter={shortDate} />
                                                                            <Bar yAxisId="l" name="Clicks" dataKey="clicks" fill={C.sageL2} radius={[2, 2, 0, 0]} maxBarSize={14} />
                                                                            <Line yAxisId="r" name="Installs" type="monotone" dataKey="installs" stroke={C.rust} strokeWidth={2} dot={{ r: 2 }} />
                                                                            <Line yAxisId="r" name="Trials" type="monotone" dataKey="trials" stroke={C.slate} strokeWidth={2} dot={{ r: 2 }} />
                                                                        </ComposedChart>
                                                                    </ResponsiveContainer>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </TableWrap>
                            {!showTrials && (
                                <div style={{ fontFamily: FONT, fontSize: 11, color: C.tick }}>Trials and {"£"}/trial are hidden: no ad has a trial in this window.</div>
                            )}
                        </Card>
                )}
            </>) : (
                <Empty>No ad data yet. It flows in automatically once the META_AD_ACCOUNT_ID secret is set in GitHub and the daily pull has run - the collector backfills the last 30 days of spend, clicks and installs per campaign.</Empty>
            )}

            {/* ---------------- 05 posts */}
            <SectionHead id="posts" n="05" label="Posts" title={`Posts (${fmt(sorted.length)})`} />
            <Card>
                <TableWrap minWidth={1080}>
                    <thead>
                        <tr>
                            {postTh("Date", "date", "left")}
                            {postTh("Title", "title", "left")}
                            {postTh("Acct", "account", "left")}
                            {postTh("Cat", "category", "left")}
                            {postTh("Views", "views")}{postTh("Reach", "reach")}{postTh("Likes", "likes")}
                            {postTh("Follows", "follows")}{postTh("Saves", "saves")}{postTh("Shares", "shares")}
                            {postTh("Skip%", "skip")}{postTh("Watch s", "watch")}{postTh("Visits", "visits")}{postTh("Vis/1k", "visitsPer1k")}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map(p => (
                            <tr key={p.id} className="hq-row">
                                <td style={{ ...td(), color: C.muted, whiteSpace: "nowrap" }}>{shortDate(p.day)}</td>
                                <td style={{ ...td(), maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }} title={p.title}>{p.title}</td>
                                <td style={{ ...td(), whiteSpace: "nowrap" }}>
                                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: acctColor(p.account), marginRight: 6 }} />
                                    {p.account}
                                </td>
                                <td style={{ ...td(), color: "rgba(22,25,37,.6)", whiteSpace: "nowrap" }}>{p.category || "-"}</td>
                                <td style={tdNum()}>{fmt(p.views)}</td>
                                {[p.reach, p.likes, p.follows, p.saves, p.shares].map((v, i) =>
                                    <td key={i} style={tdNum(500)}>{fmt(v)}</td>)}
                                <td style={tdNum(500)}>{fmt(p.skip)}</td>
                                <td style={tdNum(500)}>{fmt(p.watch, 1)}</td>
                                <td style={tdNum(500)}>{fmt(p.visits)}</td>
                                <td style={td("right")}>
                                    <span style={{ ...numStyle(15), padding: "1px 6px", borderRadius: 6, background: (p.visitsPer1k ?? 0) >= 10 ? "rgba(144,155,123,.25)" : "transparent" }}>
                                        {fmt(p.visitsPer1k, 1)}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </TableWrap>
                {!sorted.length && <Empty>No posts match these filters.</Empty>}
            </Card>
        </Shell>
    );
}
